import {
  type FieldValues,
  useController,
  type UseControllerProps,
} from 'react-hook-form';
import { TabsProps, Tabs, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

export type TabsFieldProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<TabsProps, 'value' | 'defaultValue'>;

/**
 * TabsField component
 *
 * @example
 * ```tsx
 * <TabsField name="field.name" >
 *   <Tabs.List>
 *     <Tabs.Tab value='tab1'>label 1</Tabs.Tab>
 *     <Tabs.Tab value='tab2'>label 2</Tabs.Tab>
 *     <Tabs.Tab value='tab3'>label 3</Tabs.Tab>
 *   </Tabs.List>
 * </TabsField>
 * ```
 *
 * @public
 * @param name - field name
 * @param control - form control
 * @param defaultValue - default value
 * @param rules - validation rules
 * @param shouldUnregister - unregister field when unmount
 * @param onChange - onChange event
 * @param props - other props
 */
export function TabsField<T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  children,
  ...props
}: TabsFieldProps<T>) {
  const { t } = useTranslation();
  const {
    field: { value, onChange: fieldOnChange, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });

  return (
    <>
      <Tabs
        value={value}
        onTabChange={tabValue => fieldOnChange(tabValue)}
        {...field}
        {...props}
      >
        {children}
      </Tabs>
      {fieldState.error?.message && (
        <Text color="red" mt="xs">
          {t(fieldState.error.message)}
        </Text>
      )}
    </>
  );
}
